import React, { useState, useEffect, useRef } from 'react';
import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, doc, setDoc, updateDoc, getDoc } from 'firebase/firestore';
import './Chatbot.css';

// UI feedback sounds (bot response chime + user submit blip)

// Chatbot steps for staff app
const STEPS = {
  WELCOME: 'welcome',
  STAFF_LOOKUP: 'staff_lookup',
  CONFIRM_STAFF: 'confirm_staff',
  ATTENDING: 'attending',
  GUEST_COUNT: 'guest_count',
  GUEST_DETAILS: 'guest_details',
  PRIVACY_SETTING: 'privacy_setting',
  CONFIRM_RSVP: 'confirm_rsvp',
  THANK_YOU: 'thank_you',
  ERROR: 'error',
  SAVING: 'saving',
  REDACTED_RSVP: 'redacted_rsvp',
  CONFIRMED_RSVP: 'confirmed_rsvp'
};

function Chatbot({ onStaffLookup, onRsvpSubmit, staffData, rsvpData, loading, errorMessage, isStaffApp, isAuthor }) {
  // State
  const [currentStep, setCurrentStep] = useState(STEPS.WELCOME);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [disableInput, setDisableInput] = useState(false);
  const [tempRsvpData, setTempRsvpData] = useState({
    attending: true,
    nameConfirmed: false,
    guestCount: 0,
    guests: [],
    privacyEnabled: false,
    updatedAt: null,
    createdAt: null,
    completedSteps: []
  });
  const [currentGuest, setCurrentGuest] = useState(0);
  const [guestDetailStep, setGuestDetailStep] = useState('name'); // 'name' or 'relationship'
  const [isSaving, setIsSaving] = useState(false);
  
  // Refs
  const chatEndRef = useRef(null);
  const initialMessageShown = useRef(false);
  const staffConfirmationShown = useRef(false);
  const staffLookupPromptShown = useRef(false);
  const userSelectedGuestCountRef = useRef(0);
  
  // Firebase references
  const dbRef = useRef(null);
  const sessionIdRef = useRef(null);
  
  // Initialize Firebase on component mount
  useEffect(() => {
    try {
      // Use the same Firebase project as the student app
      const firebaseConfig = {
        apiKey: "AIzaSyA2XNcUUXbGT9LDWVtSHUlZtl0F0dYEhYM",
        authDomain: "cte-rsvp.firebaseapp.com",
        projectId: "cte-rsvp",
        storageBucket: "cte-rsvp.firebasestorage.app",
        messagingSenderId: "741299085271",
        appId: "1:741299085271:web:a348019a274debfb60b592"
      };
      
      const app = initializeApp(firebaseConfig, 'staff-app');
      dbRef.current = getFirestore(app);
      console.log("Firebase initialized successfully");
    } catch (error) {
      console.error("Firebase initialization error:", error);
    }
  }, []);
  
  // Debug function to log current state
  const logState = (message) => {
    console.log(`${message}: `, {
      currentStep,
      guestCount: tempRsvpData.guestCount,
      guests: tempRsvpData.guests,
      currentGuest,
      guestDetailStep,
      sessionId: sessionIdRef.current,
      privacyEnabled: tempRsvpData.privacyEnabled,
      isAuthor: isAuthor,
      isRedacted: rsvpData?.isRedacted
    });
  };
  
  // Helper function to retrieve data from Firestore
  const fetchLatestData = async () => {
    if (!dbRef.current || !staffData?.username) {
      console.warn("Cannot fetch data - Firebase or staff username not available");
      return null;
    }
    
    try {
      const docRef = doc(collection(dbRef.current, "staffRsvps"), staffData.username);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log("Fetched latest data:", data);
        return data;
      } else {
        console.warn("No document exists with staff username:", staffData.username);
        return null;
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      return null;
    }
  };
  
  // Initialize Firebase session
  const initializeFirebaseSession = async (staffData) => {
    if (!dbRef.current) {
      console.error("Firebase Firestore not available");
      return false;
    }
    
    // Create a session ID if not already created
    if (!sessionIdRef.current) {
      sessionIdRef.current = "staff_session_" + Date.now() + "_" + Math.random().toString(36).substring(2, 9);
    }
    
    // Initialize the RSVP data with staff information
    const initialData = {
      staffUsername: staffData?.username || null,
      staffName: staffData?.name || null,
      staffEmail: staffData?.email || null,
      staffTitle: staffData?.title || null,
      attending: true,
      nameConfirmed: false,
      guestCount: 0,
      guests: [],
      privacyEnabled: false,
      updatedAt: new Date(),
      createdAt: new Date(),
      status: "in_progress",
      completedSteps: []
    };
    
    // Create the document in Firestore
    try {
      // Use the staffRsvps collection for the session with username as ID
      const sessionRef = doc(collection(dbRef.current, "staffRsvps"), staffData.username);
      
      // Check if document already exists
      const docSnap = await getDoc(sessionRef);
      
      if (!docSnap.exists()) {
        // Only create if it doesn't exist already
        await setDoc(sessionRef, initialData);
        console.log("Firebase session initialized:", staffData.username);
      } else {
        console.log("Session already exists for:", staffData.username);
      }
      return true;
    } catch (error) {
      console.error("Error creating session:", error);
      return false;
    }
  };
  
  // Save progress to Firestore
  const saveToFirestore = async (updatedData = {}) => {
    // If Firebase not available, use onRsvpSubmit as fallback
    if (!dbRef.current || !staffData?.username) {
      console.warn("Firebase or staff username unavailable, using fallback save method");
      
      const mergedData = {
        ...tempRsvpData,
        ...updatedData,
        updatedAt: new Date()
      };
      
      try {
        const success = await onRsvpSubmit(mergedData);
        return success;
      } catch (error) {
        console.error("Error in fallback save:", error);
        return false;
      }
    }
    
    // Show saving state
    setIsSaving(true);
    addBotMessage({
      text: "Saving your information...",
      delay: 0
    });
    
    // Add timestamp and merge with current data
    const dataToSave = {
      ...updatedData,
      updatedAt: new Date()
    };
    
    // Make sure privacy is explicitly a boolean
    if ('privacyEnabled' in dataToSave) {
      dataToSave.privacyEnabled = dataToSave.privacyEnabled === true;
    }
    
    // Use staffRsvps collection and username as document ID
    try {
      const docRef = doc(collection(dbRef.current, "staffRsvps"), staffData.username);
      
      // Check if document exists
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        await updateDoc(docRef, dataToSave);
      } else {
        // Create new document if it doesn't exist
        await setDoc(docRef, {
          ...tempRsvpData,
          ...dataToSave,
          createdAt: new Date()
        });
      }
      
      console.log("Data saved successfully:", dataToSave);
      
      // Show success message
      addBotMessage({
        text: "Information saved!",
        delay: 200
      });
      
      // Update local state
      setTempRsvpData(prev => ({
        ...prev,
        ...dataToSave
      }));
      
      setIsSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving to Firestore:", error);
      
      // Show error but continue
      addBotMessage({
        text: "There was an issue saving your information, but we'll continue.",
        delay: 200
      });
      
      setIsSaving(false);
      return false;
    }
  };
  
  // Clear all messages and reset the chat
  const resetChat = () => {
    setMessages([]);
    initialMessageShown.current = false;
    staffConfirmationShown.current = false;
    staffLookupPromptShown.current = false;
    setIsSaving(false);
    setCurrentStep(STEPS.WELCOME);
    
    // Reset RSVP data
    setTempRsvpData({
      attending: true,
      nameConfirmed: false,
      guestCount: 0,
      guests: [],
      privacyEnabled: false,
      updatedAt: null,
      createdAt: null,
      completedSteps: []
    });
    
    setCurrentGuest(0);
    setGuestDetailStep('name');
    userSelectedGuestCountRef.current = 0;
    
    // Show welcome message
    setTimeout(() => {
      addBotMessage({
        text: "Welcome to the Veterans Memorial High School CTE Senior Awards Ceremony Staff RSVP.",
        delay: 0
      });
      
      // If we don't have staff data, show staff lookup prompt
      if (!staffData) {
        staffLookupPromptShown.current = true;
        addBotMessage({
          text: "Please enter your district username or email address to get started with your RSVP.",
          delay: 500
        });
        setCurrentStep(STEPS.STAFF_LOOKUP);
      }
    }, 100);
  };
  
  // Initialize with existing RSVP data if available
  useEffect(() => {
    if (rsvpData) {
      console.log("Setting initial RSVP data:", rsvpData);
      setTempRsvpData({
        ...rsvpData,
        nameConfirmed: true,
        privacyEnabled: rsvpData.privacyEnabled === true
      });
      if (typeof rsvpData.guestCount === 'number') {
        userSelectedGuestCountRef.current = rsvpData.guestCount;
      }
    }
  }, [rsvpData]);
  
  // Set initial welcome message
  useEffect(() => {
    if (!initialMessageShown.current) {
      initialMessageShown.current = true;
      addBotMessage({
        text: "Welcome to the Veterans Memorial High School CTE Senior Awards Ceremony Staff RSVP.",
        delay: 500
      });
    }
  }, []);
  
  // Handle staff data from URL or lookup input
  useEffect(() => {
    // Only proceed if not loading
    if (loading) return;
    
    // Skip if staffConfirmation already shown for this staff member
    if (staffData && !staffConfirmationShown.current) {
      staffConfirmationShown.current = true;
      
      // Initialize Firebase session with staff data
      if (dbRef.current && !sessionIdRef.current) {
        initializeFirebaseSession(staffData);
      }
      
      // Ask for staff confirmation
      setCurrentStep(STEPS.CONFIRM_STAFF);
      addBotMessage({
        text: `Hello ${staffData.name}! Is this the right staff member we're talking to?`,
        options: ["Yes, that's me!", "No, I'm someone else"],
        delay: 500
      });
    } 
    // Show staff lookup prompt if no staff data and not already shown
    else if (!staffData && !staffLookupPromptShown.current && initialMessageShown.current && currentStep === STEPS.WELCOME) {
      staffLookupPromptShown.current = true;
      addBotMessage({
        text: "Please enter your district username or email address to get started with your RSVP.",
        delay: 500
      });
      setCurrentStep(STEPS.STAFF_LOOKUP);
    }
  }, [staffData, loading, currentStep]);
  
  // Handle error messages
  useEffect(() => {
    if (errorMessage && !loading) {
      addBotMessage({
        text: errorMessage,
        delay: 500
      });
      
      // Reset to ask for staff lookup again
      setCurrentStep(STEPS.STAFF_LOOKUP);
    }
  }, [errorMessage, loading]);
  
  // Scroll to bottom whenever messages change
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Add a message from the bot
  const addBotMessage = (messageObj) => {
    const { text, options, delay = 0, isHTML = false } = messageObj;
    
    setTimeout(() => {
      setMessages(prev => [
        ...prev, 
        { 
          id: Date.now(), 
          sender: 'bot', 
          text, 
          options,
          isHTML
        }
      ]);
      
      // Only keep input disabled for option messages
      setDisableInput(options ? true : false);
    }, delay);
  };
  
  // Add a message from the user
  const addUserMessage = (text) => {
    setMessages(prev => [
      ...prev, 
      { 
        id: Date.now(), 
        sender: 'user', 
        text 
      }
    ]);
  };
  
  // Handle option button clicks
  const handleOptionClick = (option) => {
    // Prevent interaction while saving
    if (isSaving) {
      return;
    }
    
    addUserMessage(option);
    setDisableInput(false);
    
    switch(currentStep) {
      case STEPS.CONFIRM_STAFF:
        handleConfirmStaff(option);
        break;
      case STEPS.ATTENDING:
        handleAttending(option);
        break;
      case STEPS.PRIVACY_SETTING:
        handlePrivacySetting(option);
        break;
      case STEPS.CONFIRM_RSVP:
        handleConfirmRsvp(option);
        break;
      case STEPS.REDACTED_RSVP:
        if (option === "Yes, create my own RSVP") {
          // Try to load the user's own username from localStorage
          const myUsername = localStorage.getItem('myStaffUsername');
          if (myUsername) {
            // Load their own RSVP
            onStaffLookup(myUsername);
          } else {
            // Ask them to enter their username
            resetChat();
          }
        } else {
          // Go back
          window.history.pushState(null, '', '/');
          onStaffLookup(null);
          resetChat();
        }
        break;
      case STEPS.CONFIRMED_RSVP:
        if (option === "Yes, create a new RSVP") {
          // Create a new document ID for this staff member or reset the current one
          // This would normally be handled with a proper database operation
          // For now, we'll just reset and start a new one
          resetChat();
          
          // Wait for reset to complete, then re-lookup this staff member
          setTimeout(() => {
            onStaffLookup(staffData.username || staffData.username);
          }, 1000);
        } else {
          // Keep existing RSVP
          addBotMessage({
            text: "Your existing RSVP has been kept. Thank you!",
            delay: 500
          });
          
          addBotMessage({
            text: `
              <div class="ceremony-details">
                <h3>Itinerary</h3>
                <p><strong>Date:</strong> Monday, May 4, 2026</p>
                <p><strong>Time:</strong> 6:00 PM to 8:00 PM</p>
                <p><strong>Location:</strong> Veterans Memorial High School Auditorium</p>
                <p><strong>Reception:</strong> Following the ceremony, in the cafeteria</p>
                <p><strong>Dress Code:</strong> Semi-formal</p>
                <p class="note">Thank you for supporting our CTE seniors. If you have photos or video clips from CTE programs, competitions, or events students were part of, share them for the ceremony slideshow.</p>
                <a href="https://cte-rsvp.joshuabailey.us/upload" class="cta-button" target="_blank" rel="noopener noreferrer">Upload your memories &rarr;</a>
              </div>
            `,
            isHTML: true,
            delay: 1000
          });

          setCurrentStep(STEPS.THANK_YOU);
        }
        break;
      default:
        break;
    }
  };
  
  // Handle staff confirmation
  const handleConfirmStaff = (option) => {
    if (option === "Yes, that's me!") {
      // Update local state
      setTempRsvpData(prev => ({
        ...prev,
        nameConfirmed: true
      }));
      
      // Check if they've already RSVP'd. An in-progress stub doc is created on
      // first visit by initializeFirebaseSession, so we gate this on status to
      // avoid showing default values (e.g. attending: true) as a real answer.
      if (rsvpData && rsvpData.status === "confirmed") {
        addBotMessage({
          text: `Great! I see you've already RSVP'd. You can review your information.`,
          delay: 500
        });
        
        // Show them their existing RSVP information
        let html = `
          <div class="rsvp-summary">
            <h3>Your RSVP</h3>
            <p><strong>Attending:</strong> ${rsvpData.attending ? 'Yes' : 'No'}</p>
        `;
        
        html += `<p><strong>Number of Guests:</strong> ${rsvpData.guestCount}</p>`;
        
        // Check if this is a redacted RSVP (private and confirmed)
        if (rsvpData.isRedacted) {
          html += `
            <p class="privacy-notice" style="color: var(--horizon-gold); font-weight: bold;">
              ${rsvpData.redactionReason || "This RSVP has been set to private. Guest details are hidden."}
            </p>
          `;
        } else {
          // Add guest information if available and not redacted
          if (rsvpData.guests && rsvpData.guests.length > 0) {
            rsvpData.guests.forEach((guest, i) => {
              html += `
                <div class="guest-item">
                  <p><strong>Guest ${i+1}:</strong> ${guest.name || 'Not specified'}</p>
                  <p><strong>Title/Relationship:</strong> ${guest.relationship || 'Not specified'}</p>
                </div>
              `;
            });
          }
        }
        
        // Add privacy setting
        html += `<p><strong>Privacy Setting:</strong> ${rsvpData.privacyEnabled ? 'Private' : 'Standard'}</p>`;
        
        // Check if this RSVP is already confirmed
        const isConfirmed = rsvpData.status === "confirmed";
        
        // Format date properly - handle both Firestore timestamps and regular dates
        let dateString = '';
        if (rsvpData.updatedAt) {
          if (typeof rsvpData.updatedAt.toDate === 'function') {
            dateString = rsvpData.updatedAt.toDate().toLocaleDateString();
          } else if (rsvpData.updatedAt instanceof Date) {
            dateString = rsvpData.updatedAt.toLocaleDateString();
          }
        }
        
        html += `
            <p><small>Last updated: ${dateString}</small></p>
          </div>
        `;
        
        addBotMessage({
          text: html,
          isHTML: true,
          delay: 1000
        });
        
        // Privacy-redacted RSVPs are permanently locked; everything else can
        // be edited (including flipping attending Yes <-> No) via the same
        // CONFIRM_RSVP path the student wizard uses.
        if (isConfirmed && rsvpData.isRedacted) {
          addBotMessage({
            text: "This RSVP has been confirmed with privacy enabled. Guest details are permanently hidden. Would you like to create a new RSVP?",
            options: ["Yes, create a new RSVP", "No, keep existing RSVP"],
            delay: 1500
          });
          setCurrentStep(STEPS.CONFIRMED_RSVP);
        } else {
          addBotMessage({
            text: "Would you like to make changes to your RSVP?",
            options: ["Yes, update my RSVP", "No, keep it as is"],
            delay: 1500
          });
          setCurrentStep(STEPS.CONFIRM_RSVP);
        }
      } else {
        // New RSVP - save confirmation and ask attendance first
        saveToFirestore({
          nameConfirmed: true,
          guestCount: 0
        }).then(() => {
          setCurrentStep(STEPS.ATTENDING);
          askAttending();
        });
      }
    } else if (option === "No, I'm someone else") {
      // Not the right staff member - clear the URL and staff data
      window.history.pushState(null, '', '/');
      
      // Clear staff data in parent component
      onStaffLookup(null);
      
      // Reset session ID
      sessionIdRef.current = null;
      
      // Reset all refs and state
      staffConfirmationShown.current = false;
      resetChat();
    }
  };
  
  // Ask whether the staff member will attend the ceremony.
  const askAttending = () => {
    addBotMessage({
      text: "Will you be able to attend the ceremony on Monday, May 4?",
      options: ["Yes, I'll be there", "No, I can't make it"],
      delay: 500
    });
  };

  // Handle the attending answer.
  const handleAttending = async (option) => {
    if (option === "Yes, I'll be there") {
      setTempRsvpData(prev => ({
        ...prev,
        attending: true,
        completedSteps: prev.completedSteps.includes("attending")
          ? prev.completedSteps
          : [...prev.completedSteps, "attending"]
      }));

      await saveToFirestore({
        attending: true,
        completedSteps: tempRsvpData.completedSteps.includes("attending")
          ? tempRsvpData.completedSteps
          : [...tempRsvpData.completedSteps, "attending"]
      });

      setCurrentStep(STEPS.GUEST_COUNT);
      askGuestCount();
    } else if (option === "No, I can't make it") {
      // Save a complete "not attending" RSVP and go straight to thank-you.
      setIsSaving(true);

      const finalCompletedSteps = [...tempRsvpData.completedSteps, "attending", "confirmation"];
      const submittedAt = new Date();
      const notAttendingPayload = {
        attending: false,
        guestCount: 0,
        guests: [],
        privacyEnabled: false,
        status: "confirmed",
        completedSteps: finalCompletedSteps,
        submittedAt
      };

      setTempRsvpData(prev => ({ ...prev, ...notAttendingPayload }));

      await saveToFirestore(notAttendingPayload);

      try {
        await onRsvpSubmit({
          ...tempRsvpData,
          ...notAttendingPayload
        });
      } catch (err) {
        console.error("Failed to save not-attending staff RSVP:", err);
      }

      setIsSaving(false);
      setCurrentStep(STEPS.THANK_YOU);
      updateStatusIndicator("confirmed");
      updateProgressBar(100);

      addBotMessage({
        text: "Thanks for letting us know — we'll miss you. If your plans change, just come back to this page to update your RSVP.",
        delay: 500
      });
    }
  };

  // Ask for guest count
  const askGuestCount = () => {
    addBotMessage({
      text: `Great! How many guests will you be bringing to the ceremony? Please enter a number.`,
      delay: 500
    });
  };
  
  // Handle guest count response
  const handleGuestCount = async (count) => {
    // Prevent multiple submissions
    if (isSaving) return;
    
    // Disable input during processing
    setDisableInput(true);
    setIsSaving(true);
    
    // Ensure count is treated as a number
    let numGuests = parseInt(count);
    
    // Validate input
    if (isNaN(numGuests) || numGuests < 0) {
      addBotMessage({
        text: "Please enter a valid number of guests (0 or higher).",
        delay: 200
      });
      setIsSaving(false);
      setDisableInput(false);
      return;
    }
    
    console.log('Setting guest count to:', numGuests);
    
    // Store the user's selection in a ref for later use
    userSelectedGuestCountRef.current = numGuests;
    
    // Create a properly sized array for the guests
    const guestsArray = [];
    for (let i = 0; i < numGuests; i++) {
      if (tempRsvpData.guests && tempRsvpData.guests[i]) {
        guestsArray.push({...tempRsvpData.guests[i]});
      } else {
        guestsArray.push({name: '', relationship: ''});
      }
    }
    
    // Update local state first
    setTempRsvpData(prev => ({
      ...prev,
      guestCount: numGuests,
      guests: guestsArray,
      completedSteps: prev.completedSteps.includes("guest_count") 
        ? prev.completedSteps 
        : [...prev.completedSteps, "guest_count"]
    }));
    
    // Save to Firebase
    try {
      await saveToFirestore({
        guestCount: numGuests,
        guests: guestsArray,
        completedSteps: tempRsvpData.completedSteps.includes("guest_count") 
          ? tempRsvpData.completedSteps 
          : [...tempRsvpData.completedSteps, "guest_count"]
      });
      
      // Add message about guest count
      addBotMessage({
        text: `You've indicated you'll bring ${numGuests} guest${numGuests !== 1 ? 's' : ''}.`,
        delay: 200
      });
      
      // Reset guest detail tracking
      setCurrentGuest(0);
      setGuestDetailStep('name');
      
      setIsSaving(false);
      setDisableInput(false);
      
      // Directly decide next step based on local guest count
      if (numGuests > 0) {
        console.log('Moving to GUEST_DETAILS step with', numGuests, 'guests');
        setCurrentStep(STEPS.GUEST_DETAILS);
        setTimeout(() => askGuestDetails(0, 'name'), 500);
      } else {
        console.log('No guests, moving to privacy setting');
        setCurrentStep(STEPS.PRIVACY_SETTING);
        setTimeout(() => askPrivacySetting(), 500);
      }
    } catch (error) {
      console.error("Error in handleGuestCount:", error);
      setIsSaving(false);
      setDisableInput(false);
      
      addBotMessage({
        text: "Sorry, there was an issue processing your request. Let's try again.",
        delay: 200
      });
      
      // Add guest count options again
      setTimeout(() => askGuestCount(), 500);
    }
  };
  
  // Ask for guest details (name or relationship)
  const askGuestDetails = (guestIndex, detailType) => {
    console.log(`Asking for guest ${guestIndex} ${detailType}, total guests: ${tempRsvpData.guestCount}`);
    
    // Safety check - make sure we don't go beyond guest count
    if (guestIndex >= userSelectedGuestCountRef.current) {
      console.log(`Guest index ${guestIndex} exceeds guest count ${userSelectedGuestCountRef.current}, moving to privacy setting`);
      setCurrentStep(STEPS.PRIVACY_SETTING);
      askPrivacySetting();
      return;
    }
    
    // Make absolutely sure we have valid guest data to work with
    if (!tempRsvpData.guests || !Array.isArray(tempRsvpData.guests) || tempRsvpData.guests.length <= guestIndex) {
      console.error("Missing or invalid guests array:", tempRsvpData.guests);
      
      // Create a properly sized array if missing
      const newGuestsArray = [];
      for (let i = 0; i < userSelectedGuestCountRef.current; i++) {
        if (tempRsvpData.guests && tempRsvpData.guests[i]) {
          newGuestsArray.push({...tempRsvpData.guests[i]});
        } else {
          newGuestsArray.push({name: '', relationship: ''});
        }
      }
      
      console.log("Created new guests array:", newGuestsArray);
      
      // Update state with the new array
      setTempRsvpData(prev => ({
        ...prev,
        guests: newGuestsArray
      }));
      
      if (detailType === 'name') {
        addBotMessage({
          text: `Please enter the name of Guest #${guestIndex + 1}:`,
          delay: 500
        });
        
        setGuestDetailStep('name');
      } else if (detailType === 'relationship') {
        addBotMessage({
          text: `What is this person's title/relationship? (e.g., Spouse, Principal, District Admin)`,
          delay: 500
        });
        
        setGuestDetailStep('relationship');
      }
      
      return;
    }
    
    const guest = tempRsvpData.guests[guestIndex];
    console.log(`Current guest data:`, guest);
    
    if (detailType === 'name') {
      // Display existing name if available
      const namePrompt = guest && guest.name 
        ? `Current name for Guest #${guestIndex + 1} is "${guest.name}". Enter a new name or press Enter to keep it:` 
        : `Please enter the name of Guest #${guestIndex + 1}:`;
      
      addBotMessage({
        text: namePrompt,
        delay: 500
      });
      
      // Set state to track what we're asking for
      setGuestDetailStep('name');
    } else if (detailType === 'relationship') {
      // Display existing relationship if available
      const relationshipPrompt = guest && guest.relationship 
        ? `Current title/relationship for ${guest.name || 'this person'} is "${guest.relationship}". Enter a new title/relationship or press Enter to keep it:` 
        : `What is ${guest.name || 'this person'}'s title/relationship? (e.g., Spouse, Principal, District Admin)`;
      
      addBotMessage({
        text: relationshipPrompt,
        delay: 500
      });
      
      // Set state to track what we're asking for
      setGuestDetailStep('relationship');
    }
  };
  
  // Add a new function for privacy setting
  const askPrivacySetting = () => {
    addBotMessage({
      text: "Would you like to enable privacy for your RSVP? This will prevent others from seeing your guest information if they enter your email or username.",
      options: ["Yes, make my RSVP private", "No, keep it standard"],
      delay: 500
    });
  };
  
  // Add the privacy setting handler function
  const handlePrivacySetting = async (option) => {
    // Prevent interaction while saving
    if (isSaving) return;
    
    const isPrivate = option === "Yes, make my RSVP private";
    
    setIsSaving(true);
    
    // Update local state immediately
    setTempRsvpData(prev => ({
      ...prev,
      privacyEnabled: isPrivate,
      completedSteps: prev.completedSteps.includes("privacy_setting") 
        ? prev.completedSteps 
        : [...prev.completedSteps, "privacy_setting"]
    }));
    
    // Save to Firebase
    try {
      await saveToFirestore({
        privacyEnabled: isPrivate,
        completedSteps: tempRsvpData.completedSteps.includes("privacy_setting") 
          ? tempRsvpData.completedSteps 
          : [...tempRsvpData.completedSteps, "privacy_setting"]
      });
      
      addBotMessage({
        text: isPrivate 
          ? "Your RSVP information will be kept private." 
          : "Your RSVP will use standard visibility settings.",
        delay: 500
      });
      
      setIsSaving(false);
      
      // Move to final confirmation
      setCurrentStep(STEPS.CONFIRM_RSVP);
      setTimeout(() => showRsvpSummary(), 500);
    } catch (error) {
      console.error("Error setting privacy:", error);
      setIsSaving(false);
      
      addBotMessage({
        text: "There was an issue saving your privacy setting. Let's continue anyway.",
        delay: 500
      });
      
      // Move to confirmation despite error
      setCurrentStep(STEPS.CONFIRM_RSVP);
      setTimeout(() => showRsvpSummary(), 500);
    }
  };
  
  // Show RSVP summary
  const showRsvpSummary = async () => {
    console.log('Preparing to show summary');
    console.log('Current userSelectedGuestCount:', userSelectedGuestCountRef.current);
    console.log('Current privacy setting:', tempRsvpData.privacyEnabled);
    
    // Show loading message
    addBotMessage({
      text: "Preparing your RSVP summary...",
      delay: 200
    });
    
    try {
      // Use the ref value directly
      const finalGuestCount = userSelectedGuestCountRef.current;
      
      // Always use a properly sized guests array
      const finalGuests = [];
      for (let i = 0; i < finalGuestCount; i++) {
        if (tempRsvpData.guests && tempRsvpData.guests[i]) {
          finalGuests.push({...tempRsvpData.guests[i]});
        } else {
          finalGuests.push({name: 'Not specified', relationship: 'Not specified'});
        }
      }
      
      // Make sure we have the latest data
      const latestData = await fetchLatestData();
      
      // Refresh the privacy setting from the database if available
      const currentPrivacySetting = latestData?.privacyEnabled !== undefined 
        ? latestData.privacyEnabled 
        : tempRsvpData.privacyEnabled;
        
      // Update local state to match latest from database
      if (latestData) {
        setTempRsvpData(prev => ({
          ...prev,
          privacyEnabled: currentPrivacySetting
        }));
      }
      
      // Use the current state for the summary
      setCurrentStep(STEPS.CONFIRM_RSVP);
      
      const html = `
        <div class="rsvp-summary">
          <h3>Your RSVP Summary</h3>
          <p><strong>Name:</strong> ${staffData.name}</p>
          <p><strong>Title:</strong> ${staffData.title || 'Staff'}</p>
          <p><strong>Number of Guests:</strong> ${finalGuestCount}</p>
          ${finalGuests.length > 0 ? finalGuests.map((guest, i) => `
            <div class="guest-item">
              <p><strong>Guest ${i+1}:</strong> ${guest.name || 'Not specified'}</p>
              <p><strong>Title/Relationship:</strong> ${guest.relationship || 'Not specified'}</p>
            </div>
          `).join('') : ''}
          <p><strong>Privacy Setting:</strong> ${currentPrivacySetting 
            ? 'Private - Only you can see your RSVP details' 
            : 'Standard - Your RSVP is visible when someone enters your username/email'}</p>
        </div>
      `;
      
      addBotMessage({
        text: html,
        isHTML: true,
        delay: 300
      });
      
      addBotMessage({
        text: "Is this information correct?",
        options: ["Yes, submit my RSVP", "No, I need to make changes"],
        delay: 1000
      });
    } catch (error) {
      console.error("Error showing RSVP summary:", error);
      
      // Fall back to local state values if there's an error
      const finalGuestCount = userSelectedGuestCountRef.current;
      
      const html = `
        <div class="rsvp-summary">
          <h3>Your RSVP Summary</h3>
          <p><strong>Name:</strong> ${staffData.name}</p>
          <p><strong>Title:</strong> ${staffData.title || 'Staff'}</p>
          <p><strong>Number of Guests:</strong> ${finalGuestCount}</p>
          ${tempRsvpData.guests && tempRsvpData.guests.length > 0 ? tempRsvpData.guests.slice(0, finalGuestCount).map((guest, i) => `
            <div class="guest-item">
              <p><strong>Guest ${i+1}:</strong> ${guest.name || 'Not specified'}</p>
              <p><strong>Title/Relationship:</strong> ${guest.relationship || 'Not specified'}</p>
            </div>
          `).join('') : ''}
          <p><strong>Privacy Setting:</strong> ${tempRsvpData.privacyEnabled 
            ? 'Private - Only you can see your RSVP details' 
            : 'Standard - Your RSVP is visible when someone enters your username/email'}</p>
        </div>
      `;
      
      addBotMessage({
        text: html,
        isHTML: true,
        delay: 300
      });
      
      addBotMessage({
        text: "Is this information correct?",
        options: ["Yes, submit my RSVP", "No, I need to make changes"],
        delay: 1000
      });
    }
  };
  
  // Handle RSVP confirmation
  const handleConfirmRsvp = async (option) => {
    if (option === "Yes, submit my RSVP") {
      setIsSaving(true);
      
      // Update status to confirmed
      setTempRsvpData(prev => ({
        ...prev,
        status: "confirmed",
        completedSteps: [...prev.completedSteps, "confirmation"]
      }));
      
      // Final submit - mark as completed
      const success = await onRsvpSubmit({
        ...tempRsvpData,
        status: "confirmed",
        completedSteps: [...tempRsvpData.completedSteps, "confirmation"],
        submittedAt: new Date()
      });
      
      setIsSaving(false);
      
      if (success) {
        setCurrentStep(STEPS.THANK_YOU);
        updateStatusIndicator("confirmed");
        updateProgressBar(100);
        
        addBotMessage({
          text: `Your reservation is confirmed. We look forward to seeing you Monday, May 4 at 6:00 PM.`,
          delay: 500
        });
        
        const ceremonyDetails = `
          <div class="ceremony-details">
            <h3>Itinerary</h3>
            <p><strong>Date:</strong> Monday, May 4, 2026</p>
            <p><strong>Time:</strong> 6:00 PM to 8:00 PM</p>
            <p><strong>Location:</strong> Veterans Memorial High School Auditorium</p>
            <p><strong>Reception:</strong> Following the ceremony, in the cafeteria</p>
            <p><strong>Dress Code:</strong> Semi-formal</p>
            <p class="note">Thank you for supporting our CTE seniors. If you have photos or video clips from CTE programs, competitions, or events students were part of, share them for the ceremony slideshow.</p>
            <a href="https://cte-rsvp.joshuabailey.us/upload" class="cta-button" target="_blank" rel="noopener noreferrer">Upload your memories &rarr;</a>
          </div>
        `;

        addBotMessage({
          text: ceremonyDetails,
          isHTML: true,
          delay: 1000
        });
      } else {
        // Even if Firestore save fails, still show the success message but log the error
        setCurrentStep(STEPS.THANK_YOU);
        updateStatusIndicator("confirmed");
        updateProgressBar(100);

        addBotMessage({
          text: "Your reservation is confirmed. If anything looks off later, just come back to this page.",
          delay: 500
        });

        const ceremonyDetails = `
          <div class="ceremony-details">
            <h3>Itinerary</h3>
            <p><strong>Date:</strong> Monday, May 4, 2026</p>
            <p><strong>Time:</strong> 6:00 PM to 8:00 PM</p>
            <p><strong>Location:</strong> Veterans Memorial High School Auditorium</p>
            <p><strong>Reception:</strong> Following the ceremony, in the cafeteria</p>
            <p><strong>Dress Code:</strong> Semi-formal</p>
            <p class="note">Thank you for supporting our CTE seniors. If you have photos or video clips from CTE programs, competitions, or events students were part of, share them for the ceremony slideshow.</p>
            <a href="https://cte-rsvp.joshuabailey.us/upload" class="cta-button" target="_blank" rel="noopener noreferrer">Upload your memories &rarr;</a>
          </div>
        `;

        addBotMessage({
          text: ceremonyDetails,
          isHTML: true,
          delay: 1000
        });
      }
    } else if (option === "No, keep it as is") {
      setCurrentStep(STEPS.THANK_YOU);

      addBotMessage({
        text: "Got it. Your reservation is unchanged. See you at the ceremony.",
        delay: 500
      });

      addBotMessage({
        text: `
          <div class="ceremony-details">
            <h3>Itinerary</h3>
            <p><strong>Date:</strong> Monday, May 4, 2026</p>
            <p><strong>Time:</strong> 6:00 PM to 8:00 PM</p>
            <p><strong>Location:</strong> Veterans Memorial High School Auditorium</p>
            <p><strong>Reception:</strong> Following the ceremony, in the cafeteria</p>
            <p><strong>Dress Code:</strong> Semi-formal</p>
            <p class="note">Thank you for supporting our CTE seniors. If you have photos or video clips from CTE programs, competitions, or events students were part of, share them for the ceremony slideshow.</p>
            <a href="https://cte-rsvp.joshuabailey.us/upload" class="cta-button" target="_blank" rel="noopener noreferrer">Upload your memories &rarr;</a>
          </div>
        `,
        isHTML: true,
        delay: 1000
      });
    } else if (option === "Yes, update my RSVP") {
      // Re-enter from the attending question so users can flip the answer
      // before re-walking guest count.
      setCurrentStep(STEPS.ATTENDING);
      askAttending();
    } else {
      setCurrentStep(STEPS.ATTENDING);
      askAttending();
    }
  };
  
  // Handle input submission
  const handleInputSubmit = (e) => {
    e.preventDefault();
    
    // Prevent interaction while saving
    if (isSaving) {
      return;
    }
    
    // Don't submit empty inputs (except for keeping existing guest data)
    if (!userInput.trim() && 
        !(currentStep === STEPS.GUEST_DETAILS && 
          ((guestDetailStep === 'name' && tempRsvpData.guests[currentGuest]?.name) || 
           (guestDetailStep === 'relationship' && tempRsvpData.guests[currentGuest]?.relationship)))) {
      return;
    }
    
    // Add the user's message to the chat (empty inputs are for keeping existing values)
    if (userInput.trim()) {
      addUserMessage(userInput);
    } else {
      // For empty input to keep existing values, show a message
      const keepingValue = guestDetailStep === 'name' 
        ? tempRsvpData.guests[currentGuest]?.name 
        : tempRsvpData.guests[currentGuest]?.relationship;
      addUserMessage(`Keep "${keepingValue}"`);
    }
    
    // Handle the input based on current step
    switch (currentStep) {
      case STEPS.STAFF_LOOKUP:
        handleStaffLookupSubmit();
        break;
      case STEPS.GUEST_COUNT:
        // Handle numeric inputs for guest count
        const count = userInput.trim();
        handleGuestCount(count);
        break;
      case STEPS.GUEST_DETAILS:
        handleGuestDetailsSubmit();
        break;
      default:
        // For unhandled steps, let the user know
        addBotMessage({
          text: "Please use the provided options to continue.",
          delay: 500
        });
        break;
    }
    
    // Clear the input field
    setUserInput('');
  };
  
  // Handle staff lookup submission
  const handleStaffLookupSubmit = () => {
    const identifier = userInput.trim();
    
    if (!identifier) {
      addBotMessage({
        text: "Please enter your district username or email address.",
        delay: 500
      });
      return;
    }
    
    // Set loading state
    addBotMessage({
      text: "Looking up your information...",
      delay: 500
    });
    
    // Submit to parent component
    onStaffLookup(identifier);
  };
  
  // Handle guest details submission (name or relationship)
  const handleGuestDetailsSubmit = async () => {
    // Prevent multiple submissions
    if (isSaving) return;
    
    setDisableInput(true);
    setIsSaving(true);
    
    logState("Before guest details update");
    
    // Make a copy of the guests array
    const updatedGuests = [...tempRsvpData.guests];
    const currentGuestData = updatedGuests[currentGuest] || { name: '', relationship: '' };
    
    if (guestDetailStep === 'name') {
      // If input is not empty, update the name
      if (userInput.trim()) {
        currentGuestData.name = userInput.trim();
      }
      
      // Update the guests array
      updatedGuests[currentGuest] = currentGuestData;
      
      // Update local state
      setTempRsvpData(prev => ({
        ...prev,
        guests: updatedGuests
      }));
      
      // Save to Firebase
      const success = await saveToFirestore({
        guests: updatedGuests
      });
      
      setDisableInput(false);
      setIsSaving(false);
      
      console.log(`Updated guest ${currentGuest} name to "${currentGuestData.name}"`);
      
      // Move to asking for relationship
      setGuestDetailStep('relationship');
      askGuestDetails(currentGuest, 'relationship');
      
    } else if (guestDetailStep === 'relationship') {
      // If input is not empty, update the relationship
      if (userInput.trim()) {
        currentGuestData.relationship = userInput.trim();
      }
      
      // Update the guests array
      updatedGuests[currentGuest] = currentGuestData;
      
      // Update local state
      setTempRsvpData(prev => ({
        ...prev,
        guests: updatedGuests
      }));
      
      // Save to Firebase
      const success = await saveToFirestore({
        guests: updatedGuests
      });
      
      setDisableInput(false);
      setIsSaving(false);
      
      console.log(`Updated guest ${currentGuest} relationship to "${currentGuestData.relationship}"`);
      
      // Move to next guest or privacy setting
      const nextGuest = currentGuest + 1;
      console.log(`Moving to guest ${nextGuest} of ${userSelectedGuestCountRef.current}`);
      
      if (nextGuest < userSelectedGuestCountRef.current) {
        // More guests to process
        setCurrentGuest(nextGuest);
        setGuestDetailStep('name');
        
        // Small delay to ensure state updates
        setTimeout(() => {
          askGuestDetails(nextGuest, 'name');
        }, 250);
      } else {
        // All guests processed, ask for privacy setting
        console.log('All guests processed, moving to privacy setting');
        setCurrentStep(STEPS.PRIVACY_SETTING);
        askPrivacySetting();
      }
    }
    
    logState("After guest details update");
  };
  
  // Helpers for status indicator and progress
  const updateStatusIndicator = (status) => {
    const statusEl = document.getElementById("chatbot-status");
    if (!statusEl) return;
    
    statusEl.innerHTML = '';
    
    if (status === "pending") {
      statusEl.innerHTML = `
        <div class="status-indicator pending">
          <span class="status-icon">⌛</span>
          <span class="status-text">RSVP Pending</span>
        </div>
      `;
    } else if (status === "confirmed") {
      statusEl.innerHTML = `
        <div class="status-indicator confirmed">
          <span class="status-icon">✓</span>
          <span class="status-text">RSVP Confirmed</span>
        </div>
      `;
    }
  };
  
  const updateProgressBar = (percent) => {
    const progressFill = document.querySelector(".progress-fill");
    const progressText = document.querySelector(".progress-text");
    
    if (progressFill && progressText) {
      progressFill.style.width = `${percent}%`;
      progressText.textContent = `${Math.round(percent)}% complete`;
    }
  };
  
  const calculateProgress = () => {
    // Define total steps for progress calculation
    const totalSteps = 5; // Updated for staff app: confirm, count, details, privacy, confirmation
    const completedStepCount = tempRsvpData.completedSteps.length;
    
    // Cap at 95% until actual confirmation
    if (tempRsvpData.status !== "confirmed" && !tempRsvpData.completedSteps.includes("confirmation")) {
      return Math.min(Math.round((completedStepCount / totalSteps) * 95), 95);
    }
    
    return Math.min(Math.round((completedStepCount / totalSteps) * 100), 100);
  };
  
  return (
    <div className="chatbot-container">
      <div className="chat-messages">
        {messages.map(message => (
          <div key={message.id} className={`message ${message.sender}-message`}>
            {message.isHTML ? (
              <div dangerouslySetInnerHTML={{ __html: message.text }} />
            ) : (
              <div className="message-text">{message.text}</div>
            )}
            
            {message.options && (
              <div className="message-options">
                {message.options.map((option, index) => (
                  <button 
                    key={index} 
                    className="option-button"
                    onClick={() => handleOptionClick(option)}
                    disabled={isSaving}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>
      
      <div className="chatbot-status" id="chatbot-status">
        <div className="status-indicator pending">
          <span className="status-icon">⌛</span>
          <span className="status-text">RSVP Pending</span>
        </div>
      </div>
      
      <form className="chat-input" onSubmit={handleInputSubmit}>
        <input
          type="text"
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          placeholder={
            isSaving
              ? "Saving data, please wait..."
              : currentStep === STEPS.GUEST_DETAILS && 
                ((guestDetailStep === 'name' && tempRsvpData.guests[currentGuest]?.name) || 
                (guestDetailStep === 'relationship' && tempRsvpData.guests[currentGuest]?.relationship))
                ? "Press Enter to keep current value or type a new one..."
                : disableInput 
                  ? "Please select an option above..." 
                  : "Type your response..."
          }
          disabled={disableInput || currentStep === STEPS.THANK_YOU || isSaving}
        />
        <button 
          type="submit"
          disabled={
            isSaving ||
            disableInput || 
            (userInput.trim() === '' && 
             !(currentStep === STEPS.GUEST_DETAILS && 
               ((guestDetailStep === 'name' && tempRsvpData.guests[currentGuest]?.name) || 
                (guestDetailStep === 'relationship' && tempRsvpData.guests[currentGuest]?.relationship)))) || 
            currentStep === STEPS.THANK_YOU
          }
        >
          Send
        </button>
      </form>
    </div>
  );
}

export default Chatbot;